// Query/keyword normalisation, shared by index building and query scoring so
// both sides of a comparison are always stemmed the same way. Pure functions,
// no Nest and no Prisma — the whole retrieval module is unit-testable without
// a database.

// Two jobs here. The obvious one: a purely grammatical word shared between a
// keyword phrase ("where is my order") and an unrelated message ("is this
// available?") must not produce a match on its own.
//
// The subtler one: scoring normalises by the total IDF of the query, so every
// word a shopper types that the knowledge base has never seen drags confidence
// down. Filler like "want"/"just"/"much" is meaningless but would otherwise be
// treated as unexplained signal and penalise an otherwise perfect answer.
//
// Deliberately absent: anything that could plausibly be a real FAQ topic —
// "help", "back" ("money back"), "out" ("out of stock"), "up" ("pick up"),
// "cost", "free", "no"/"yes" where they carry meaning in context.
const STOPWORDS = new Set([
  // articles, conjunctions, prepositions
  'a', 'an', 'the', 'of', 'in', 'on', 'for', 'and', 'or', 'if', 'but', 'with',
  'from', 'about', 'at', 'by', 'as', 'so', 'to', 'then', 'than',
  // pronouns and determiners
  'i', 'me', 'my', 'we', 'us', 'our', 'you', 'your', 'it', 'its', 'this',
  'that', 'these', 'those', 'they', 'them', 'their', 'he', 'she', 'his', 'her',
  // auxiliaries and copulas
  'is', 'are', 'am', 'was', 'were', 'be', 'been', 'being', 'do', 'does', 'did',
  'have', 'has', 'had', 'can', 'could', 'will', 'would', 'shall', 'should',
  'may', 'might', 'must',
  // question words
  'how', 'what', 'when', 'where', 'which', 'who', 'whom', 'why',
  // conversational filler and empty verbs — "hi"/"hello"/"hey" deliberately
  // NOT here: a pure greeting is itself a real, answerable knowledge-base
  // entry (kb_demo_greeting), not meaningless filler to strip.
  'please', 'thanks', 'thank', 'ok', 'okay', 'sorry',
  'want', 'need', 'like', 'know', 'tell', 'get', 'got', 'just', 'really',
  'actually', 'much', 'many', 'some', 'any', 'all', 'also', 'still',
]);

// Consonants that get doubled before "-ing"/"-ed" ("shipping" → "shipp" →
// "ship"). "l" is deliberately excluded: including it would strip "calling"
// to "cal", which then can never match "call" (three letters — below the
// fuzzy threshold). Leaving it out costs us "cancelled" → "cancell", which
// fuzzy matching still resolves to "cancel" at one edit.
const DOUBLED_CONSONANT = /([bdfgkmnprt])\1$/;

// "-es" is only a genuine plural marker after a sibilant ("boxes", "batches",
// "dishes"). Everywhere else the "e" belongs to the stem, and stripping it
// does real damage in this domain: "codes" → "cod" would collide with COD
// (cash on delivery), which is an actual keyword in the seeded FAQ.
const SIBILANT_STEM = /(?:s|x|z|ch|sh)$/;

function undouble(base: string): string {
  return base.length > 3 && DOUBLED_CONSONANT.test(base) ? base.slice(0, -1) : base;
}

// A light suffix stripper rather than a full Porter stemmer: the knowledge
// base is hand-written FAQ text, so plurals and "-ing"/"-ed" cover nearly all
// the real drift ("orders" vs "order", "shipping" vs "ship", "returned" vs
// "return"). Anything it misses degrades to a fuzzy match, not a miss.
export function stem(word: string): string {
  // Short words are left alone — "cod", "upi", "pay" are whole keywords, and
  // stripping anything off them creates collisions rather than matches.
  if (word.length <= 3) return word;

  // "-us" ("status") and "-ss" ("address") only look like plurals.
  if (word.endsWith('us') || word.endsWith('ss')) return word;

  if (word.length > 4 && word.endsWith('ies')) return `${word.slice(0, -3)}y`;

  for (const suffix of ['ing', 'ed'] as const) {
    // Needs at least a 3-character stem left over, which is what stops
    // "used" → "us" and "need" → "ne".
    if (word.length > suffix.length + 2 && word.endsWith(suffix)) {
      return undouble(word.slice(0, -suffix.length));
    }
  }

  if (word.length > 4 && word.endsWith('es')) {
    const base = word.slice(0, -2);
    if (SIBILANT_STEM.test(base)) return base;
  }

  if (word.length > 3 && word.endsWith('s')) return word.slice(0, -1);

  return word;
}

export function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter((word) => word.length > 0 && !STOPWORDS.has(word))
    .map(stem)
    // Stopwords are filtered again after stemming, because stemming can land
    // on one: "wheres" → "where".
    .filter((word) => word.length > 0 && !STOPWORDS.has(word));
}
